"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/components/ui/form";
import { Mail, ArrowRight, CircleCheck, Loader, User, MessageSquare, Image as ImageIcon, X } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReviewSubmissionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 p-8 bg-card rounded-md shadow-md border border-border animate-in fade-in zoom-in duration-500">
                <div className="size-12 rounded-md bg-secondary/10 flex items-center justify-center">
                    <CircleCheck className="size-6 text-secondary" />
                </div>
                <div className="text-center space-y-1">
                    <h3 className="font-default text-lg font-medium text-foreground">Review Received</h3>
                    <p className="text-muted-foreground font-sans text-sm">{successMessage}</p>
                </div>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full max-w-xl mx-auto mt-8 space-y-6 text-left">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-sans text-sm font-medium">Full Name</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                                    <Input placeholder="John Doe" className="pl-10 h-12 bg-background border-border shadow-sm rounded-md focus-visible:ring-secondary/20 focus-visible:border-secondary transition-colors font-sans" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage className="px-1 text-xs font-sans" />
                        </FormItem>} />
                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-sans text-sm font-medium">Email Address</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                                    <Input type="email" placeholder="john@example.com" className="pl-10 h-12 bg-background border-border shadow-sm rounded-md focus-visible:ring-secondary/20 focus-visible:border-secondary transition-colors font-sans" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage className="px-1 text-xs font-sans" />
                        </FormItem>} />
            </div>

            <FormField control={control} name="review" rules={{
      required: "Please share your experience",
      minLength: {
        value: 10,
        message: "Review must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-sans text-sm font-medium">Your Review</FormLabel>
                        <FormControl>
                            <div className="relative">
                                <MessageSquare className="absolute left-3.5 top-4 size-4 text-muted-foreground" />
                                <textarea placeholder="Tell us about your project..." className="w-full min-h-[120px] pl-10 pr-4 py-3 bg-background border border-input shadow-sm rounded-md focus-visible:ring-secondary/20 focus-visible:border-secondary transition-colors font-sans outline-none text-base md:text-sm" {...field} />
                            </div>
                        </FormControl>
                        <FormMessage className="px-1 text-xs font-sans" />
                    </FormItem>} />

            <FormField control={control} name="project_image" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
                        <FormLabel className="font-sans text-sm font-medium">Project Photo (Optional)</FormLabel>
                        <FormControl>
                            <div className="space-y-4">
                                <div className="flex items-center justify-center w-full">
                                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-md cursor-pointer bg-accent/5 hover:bg-accent/10 transition-colors">
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <ImageIcon className="size-8 text-muted-foreground mb-2" />
                                            <p className="text-sm text-muted-foreground font-sans">Click to upload project photo</p>
                                        </div>
                                        <input type="file" className="hidden" accept="image/*" onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  onChange(file);
                  setImagePreview(URL.createObjectURL(file));
                }
              }} {...field} />
                                    </label>
                                </div>
                                {imagePreview && <div className="relative inline-block">
                                        <img src={imagePreview} alt="Preview" className="h-24 w-24 object-cover rounded-md border border-border" />
                                        <Button type="button" variant="destructive" size="icon" onClick={() => {
              setImagePreview(null);
              onChange(null);
            }} className="absolute -top-2 -right-2 size-6 rounded-md shadow-sm">
                                            <X className="size-3" />
                                        </Button>
                                    </div>}
                            </div>
                        </FormControl>
                        <FormMessage className="px-1 text-xs font-sans" />
                    </FormItem>} />

            <Button type="submit" disabled={isSubmitting} className="h-12 px-8 rounded-md shadow-md transition-all duration-200 w-full group font-sans bg-primary text-primary-foreground hover:bg-primary/90">
                {isSubmitting ? <Loader className="size-4 animate-spin" /> : <>
                        Submit Review
                        <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </>}
            </Button>

            {submitError && <p className="text-destructive text-sm mt-3 text-center font-sans">{submitError}</p>}
        </Form>;
}
export default function Waitlist() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="256267" id="share-your-experience" className="bg-background py-24 md:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-accent/10 pointer-events-none" aria-hidden="true" />
            
            <div ref={ref} className={`relative max-w-2xl mx-auto text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-2">
                    Tell Your Story
                </h2>
                <p className="text-secondary font-sans text-sm font-medium mb-4">
                    Reviews are moderated before appearing on our site.
                </p>
                <p className="font-sans text-lg text-muted-foreground mb-8 max-w-lg mx-auto">
                    We take pride in our craftsmanship. Share your remodeling experience with BT Remodeling and help others build their dreams.
                </p>

                <FormProvider formKey="review_form_client_praise" formId="review_form_client_praise" sectionName="share-your-experience" defaultValues={{
        name: "",
        email: "",
        review: "",
        project_image: null
      }} submitText="Submit Review" successMessage="Thank you for sharing your experience. Your review will appear once approved by our team.">
                    <ReviewSubmissionForm />
                </FormProvider>
            </div>
        </section>;
}